import React, { useEffect, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Search, SearchIconWrapper, StyledInputBase } from "./Search";

interface GenericSearchProps {
  value?: string;
  placeholder?: string;
  onSearch: (value: string) => void;
  debounceMs?: number;
  disabled?: boolean;
}

const GenericSearch: React.FC<GenericSearchProps> = ({
  value = "",
  placeholder = "View all",
  onSearch,
  debounceMs = 300,
  disabled = false,
}) => {
  const [searchText, setSearchText] = useState(value);

  const debounceRef = useRef<number | null>(null);
  const hasUserTyped = useRef(false);
  const onSearchRef = useRef(onSearch);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    setSearchText(value);
  }, [value]);

  useEffect(() => {
    if (!hasUserTyped.current) return;

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      onSearchRef.current(searchText);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [searchText, debounceMs]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    hasUserTyped.current = true;
    setSearchText(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      hasUserTyped.current = true;

      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      onSearchRef.current(searchText);
    }

    if (event.key === "Escape") {
      hasUserTyped.current = true;
      setSearchText("");
      onSearchRef.current("");
    }
  };

  return (
    <Search>
      <SearchIconWrapper>
        <SearchIcon />
      </SearchIconWrapper>
      <StyledInputBase
        placeholder={placeholder}
        inputProps={{ "aria-label": "search" }}
        value={searchText}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
    </Search>
  );
};

export default GenericSearch;
